import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'

interface ProtectedRouteProps {
  children: React.ReactNode
  allowedRoles?: Array<'admin' | 'arborist' | 'client'>
  redirectTo?: string
}

/**
 * ProtectedRoute component
 * - Redirects to login if not authenticated
 * - Optionally checks for allowed roles
 * - Preserves intended destination in location state
 */
export const ProtectedRoute = ({
  children,
  allowedRoles,
  redirectTo = '/login',
}: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading, role, passwordRecovery } = useAuth()
  const location = useLocation()

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />
  }

  // A recovery link leaves a real session behind, so nothing else would stop
  // it reaching the app. Hold it on the reset page until a password is set.
  if (passwordRecovery) {
    return <Navigate to="/reset-password" replace />
  }

  // Check role-based access if allowedRoles is specified
  if (allowedRoles && allowedRoles.length > 0 && role) {
    if (!allowedRoles.includes(role)) {
      // Redirect to unauthorized page or dashboard based on role
      const unauthorizedRedirect = role === 'client' ? '/portal' : '/dashboard'
      return <Navigate to={unauthorizedRedirect} replace />
    }
  }

  return <>{children}</>
}
